// MODIFIED: Shifted focus from deep tech specs to high-impact user benefits and peace of mind.
// Replaced redundancy with unique selling points (Offline safety, Anti-surveillance, Flexible triggers).

import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Shield, Clock, Users, CloudOff, Lock, UserCheck } from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile';

const FEATURES = [
  {
    id: 'dead-switch',
    icon: Clock,
    title: 'Background Dead Switch',
    description: 'Set your timeframe and go. Check in with a single tap. If you miss your window, your customized safety protocol automatically initiates.',
    size: 'large',
  },
  {
    id: 'protection',
    icon: Shield,
    title: 'Zero Surveillance',
    description: 'We protect you without tracking you. Your real-time location is never monitored, and all trip data is permanently wiped the second you arrive safely.',
    size: 'small',
  },
  {
    id: 'offline-proof',
    icon: CloudOff,
    title: 'Works Even If Phone Dies',
    description: 'Your countdown runs securely on our cloud. If your phone loses battery, gets broken, or loses service, your emergency protocol still executes flawlessly.',
    size: 'small',
  },
  {
    id: 'trust-groups',
    icon: Users,
    title: 'Tiered Trust Groups',
    description: 'Separate your emergency contacts into distinct circles. Notify your close family for daily routines, or specific colleagues for remote field trips.',
    size: 'large',
  },
  {
    id: 'encrypted-vault',
    icon: Lock,
    title: 'Encrypted Vault & Instructions',
    description: 'Leave behind secure messages, critical documents, or last-known coordinates. They remain completely locked and are only delivered if you fail to check in.',
    size: 'large',
  },
  {
    id: 'snooze-control',
    icon: UserCheck,
    title: 'Smart Warnings & Snooze',
    description: 'Running a bit late? Get gentle local reminders before your timer expires, with a quick-snooze option to extend your deadline and prevent false alarms.',
    size: 'small',
  },
];

export default function FeaturesGrid() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const isMobile = useIsMobile();

  return (
    <section id="features" ref={ref} className="relative py-32 md:py-40 bg-white overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: isMobile ? 0 : 0.8 }}
          className="text-center mb-20"
        >
          {/* MODIFIED: Human-centric marketing tag */}
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600 mb-4">
            Why Ping After
          </span>
          {/* MODIFIED: Emotional and practical marketing hook */}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Smart, silent protection.<br />
            <span className="text-emerald-600">On your terms</span>, not our radar.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {FEATURES.map((feature, i) => {
            const isLarge = feature.size === 'large';
            return (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: isMobile ? 0 : 0.6, delay: isMobile ? 0 : i * 0.1 }}
                className={`group relative rounded-3xl p-8 transition-all duration-500 hover:shadow-xl ${isLarge
                    ? 'md:col-span-2 bg-slate-900 text-white'
                    : 'bg-slate-50 text-slate-900 hover:bg-slate-100'
                  }`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLarge ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600'
                    }`}>
                    <feature.icon className="w-6 h-6" />
                  </div>
                </div>

                <h3 className={`text-xl font-bold mb-3 ${isLarge ? 'text-white' : 'text-slate-900'}`}>
                  {feature.title}
                </h3>
                <p className={`text-sm leading-relaxed ${isLarge ? 'text-white/60' : 'text-slate-500'}`}>
                  {feature.description}
                </p>

                {isLarge && (
                  <div className="absolute bottom-0 right-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl" />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}